import React, { useState, useEffect } from 'react';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  LinearProgress,
  InputAdornment,
  IconButton,
  Link,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Lock,
  VpnKey,
  ContactMail,
} from '@mui/icons-material';
import { USER_ROLES } from '../../config/cognitoConfig';

// Props: open, onClose, onSuccess(email, password), onSwitchToLogin, preSelectedRole

// Password validation helper
const validatePassword = (password) => {
  const checks = {
    minLength: password.length >= 8,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password),
  };
  
  const strength = Object.values(checks).filter(Boolean).length;
  const isValid = checks.minLength && checks.hasLowercase && checks.hasNumber; // Simplified for MammoTrials
  
  return { checks, strength, isValid };
};

// Format Cognito error messages to be user-friendly
const formatErrorMessage = (error) => {
  const errorMap = {
    'UsernameExistsException': 'This email is already registered. If you never verified your account, please contact support or try the "Sign In" option.',
    'InvalidPasswordException': 'Password does not meet requirements. Please check the password requirements below.',
    'InvalidParameterException': 'Please check that all fields are filled correctly.',
    'UserNotFoundException': 'User not found. Please check your email address.',
    'CodeMismatchException': 'Invalid verification code. Please try again.',
    'ExpiredCodeException': 'Verification code has expired. Please click "Resend Code" to get a new one.',
    'UserNotConfirmedException': 'Your email is not verified. Please check your email for the verification code.',
    'TooManyRequestsException': 'Too many attempts. Please wait a few minutes and try again.',
  };
  
  return errorMap[error.name] || error.message || 'An error occurred. Please try again.';
};

const SignUpForm = ({ 
  open, 
  onClose, 
  onSuccess, 
  onSwitchToLogin, 
  preSelectedRole 
}) => {
  const [step, setStep] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [role, setRole] = useState(preSelectedRole || USER_ROLES.PATIENT);
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const passwordValidation = validatePassword(password);

  // Update role when preSelectedRole changes
  useEffect(() => {
    if (preSelectedRole) {
      setRole(preSelectedRole);
    }
  }, [preSelectedRole]);

  // Show password requirements when user starts typing
  useEffect(() => {
    if (password.length > 0) {
      setShowPasswordRequirements(true);
    }
  }, [password]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!email || !password || !givenName || !familyName) {
        setError('Please fill in all required fields.');
        return;
      }

      if (!passwordValidation.isValid) {
        setError('Password does not meet requirements. Please check the requirements below.');
        return;
      }

      if (!agreeTerms) {
        setError('Please agree to the Terms of Service and Privacy Policy.');
        return;
      }

      const username = email.trim().toLowerCase();
      
      console.log('📝 Starting MammoTrials sign up...');
      const signUpResult = await signUp({
        username,
        password,
        attributes: {
          email: username,
          given_name: givenName.trim(),
          family_name: familyName.trim(),
          'custom:role': role, // Store role in custom attribute
          'custom:portal': role, // Also store for portal routing
        },
      });

      console.log('✅ Sign up successful:', signUpResult);

      if (signUpResult.isSignUpComplete) {
        // Account created and confirmed automatically
        onSuccess(email, password);
        handleClose();
      } else {
        // Verification required
        setStep('verify');
      }
      
    } catch (err) {
      console.error('❌ Sign up error:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const username = email.trim().toLowerCase();
      
      if (!username || !verificationCode) {
        setError('Please enter the verification code.');
        return;
      }

      console.log('✅ Confirming MammoTrials sign up...');
      await confirmSignUp({
        username,
        confirmationCode: verificationCode.trim(),
      });

      console.log('🎉 Sign up confirmed successfully');
      onSuccess(email, password);
      handleClose();
      
    } catch (err) {
      console.error('❌ Sign up confirmation error:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    setResendSuccess(false);

    try {
      const username = email.trim().toLowerCase();
      
      console.log('📨 Resending verification code...');
      await resendSignUpCode({ username });
      setResendSuccess(true);
      
    } catch (err) {
      console.error('❌ Resend code error:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    setStep('signup');
    setEmail('');
    setPassword('');
    setGivenName('');
    setFamilyName('');
    setRole(preSelectedRole || USER_ROLES.PATIENT);
    setVerificationCode('');
    setError('');
    setLoading(false);
    setShowPassword(false);
    setShowPasswordRequirements(false);
    setResendSuccess(false);
    setAgreeTerms(false);
    onClose();
  };

  const renderPasswordRequirements = () => {
    if (!showPasswordRequirements) return null;

    return (
      <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" display="block" gutterBottom>
          Password Requirements:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {passwordValidation.checks.minLength ? 
              <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} /> : 
              <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
            }
            <Typography variant="caption">At least 8 characters</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {passwordValidation.checks.hasLowercase ? 
              <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} /> : 
              <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
            }
            <Typography variant="caption">One lowercase letter</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {passwordValidation.checks.hasNumber ? 
              <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} /> : 
              <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
            }
            <Typography variant="caption">One number</Typography>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(passwordValidation.strength / 3) * 100} // Adjusted for 3 requirements
          sx={{ mt: 1, height: 4, borderRadius: 2 }}
          color={passwordValidation.strength >= 3 ? 'success' : passwordValidation.strength >= 2 ? 'warning' : 'error'}
        />
      </Box>
    );
  };

  const renderSignUpStep = () => (
    <Box component="form" onSubmit={handleSignUp} sx={{ mt: 2 }}>
      {/* Role Selection */}
      <FormControl fullWidth margin="normal" required>
        <InputLabel id="role-label">I am a...</InputLabel>
        <Select
          labelId="role-label"
          value={role}
          label="I am a..."
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
          startAdornment={
            <InputAdornment position="start">
              <ContactMail color="primary" />
            </InputAdornment>
          }
        >
          <MenuItem value={USER_ROLES.PATIENT}>
            <Box>
              <Typography variant="body1">Patient</Typography>
              <Typography variant="caption" color="text.secondary">
                Someone diagnosed with or at risk for breast cancer
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value={USER_ROLES.CLINICIAN}>
            <Box>
              <Typography variant="body1">Clinician</Typography>
              <Typography variant="caption" color="text.secondary">
                Healthcare provider, nurse, or medical professional
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value={USER_ROLES.RESEARCHER}>
            <Box>
              <Typography variant="body1">Researcher</Typography>
              <Typography variant="caption" color="text.secondary">
                Clinical research professional or academic researcher
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value={USER_ROLES.COMMUNITY}>
            <Box>
              <Typography variant="body1">Community Member</Typography>
              <Typography variant="caption" color="text.secondary">
                Advocate, caregiver, or community supporter
              </Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }}>
        <Chip label="Personal Information" size="small" />
      </Divider>

      {/* Name Fields */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="First Name"
          value={givenName}
          onChange={(e) => setGivenName(e.target.value)}
          margin="normal"
          required
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="primary" />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          label="Last Name"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          margin="normal"
          required
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Email */}
      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        helperText="This will be your username"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="primary" />
            </InputAdornment>
          ),
        }}
      />

      {/* Password */}
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {renderPasswordRequirements()}

      {/* Terms Agreement */}
      <FormControlLabel
        control={
          <Checkbox 
            checked={agreeTerms} 
            onChange={(e) => setAgreeTerms(e.target.checked)}
            disabled={loading}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Link href="/terms" target="_blank" underline="hover">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link href="/privacy" target="_blank" underline="hover">
              Privacy Policy
            </Link>
          </Typography>
        }
        sx={{ mt: 2 }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !passwordValidation.isValid || !agreeTerms}
        sx={{ mt: 3, mb: 2, height: 48 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
      </Button>

      <Typography variant="body2" align="center">
        Already have an account?{' '}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToLogin}
          disabled={loading}
          sx={{ textDecoration: 'none', fontWeight: 'bold' }}
        >
          Sign In
        </Link>
      </Typography>
    </Box>
  );

  const renderVerifyStep = () => (
    <Box sx={{ mt: 2 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          We've sent a verification code to <strong>{email}</strong>.
          Please enter the code below to complete your registration.
        </Typography>
      </Alert>

      <Box component="form" onSubmit={handleVerifySignUp}>
        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          margin="normal"
          required
          disabled={loading}
          placeholder="Enter 6-digit code"
          inputProps={{ maxLength: 6 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKey color="primary" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || !verificationCode}
          sx={{ mt: 3, mb: 2, height: 48 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Complete Registration'}
        </Button>
      </Box>

      {resendSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          A new verification code has been sent to your email.
        </Alert>
      )}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Didn't receive the code?
        </Typography>
        <Button
          variant="text"
          onClick={handleResendCode}
          disabled={loading}
          size="small"
        >
          {loading ? 'Sending...' : 'Resend Code'}
        </Button>
      </Box>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep('signup')}
        disabled={loading}
        sx={{ mt: 1 }}
      >
        Back to Registration
      </Button>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {step === 'signup' ? 'Join MammoTrials' : 'Verify Your Email'}
        </Typography>
        {step === 'signup' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create your account to access clinical trials and research opportunities
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'signup' ? renderSignUpStep() : renderVerifyStep()}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pt: 0 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignUpForm;